import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSessionId() {
  const existing = localStorage.getItem("emg_chat_sid");
  if (existing) return existing;
  const sid = `sid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem("emg_chat_sid", sid);
  return sid;
}

const WELCOME = {
  role: "assistant",
  content:
    "Bonjour 👋 Je suis Émi, l'assistante d'EM Group. Je peux vous aider à choisir l'offre qui vous correspond : branding, site web, audit ou accompagnement complet. Quel est votre projet ?",
};

const QUICK_REPLIES = [
  "Je veux un site web",
  "J'ai besoin d'un logo",
  "J'ai déjà un site",
  "Accompagnement complet",
];

// Rotating teaser messages
const TEASER_MESSAGES = [
  "Besoin d'aide pour choisir votre offre ?",
  "Je peux vous aider à trouver la bonne formule 💡",
  "Vous avez déjà un site ? Demandez un audit",
  "Besoin d'un devis rapide ?",
];

// Intervals (ms)
const FIRST_APPEAR_DELAY = 6000; // initial reveal after 6s
const TEASER_VISIBLE_DURATION = 8000; // teaser auto-hides after 8s
const MIN_INTERVAL_AFTER_DISMISS = 90000; // 90s wait if user closed it
const MIN_INTERVAL_BETWEEN_TEASERS = 45000; // else 45s minimum between appearances
const IDLE_REMINDER_MS = 50000; // reminder if user idle 50s
const SCROLL_TRIGGER_RATIO = 0.4; // 40% scroll triggers if eligible

export default function ChatBot() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [teaserIdx, setTeaserIdx] = useState(0);
  const [showTeaser, setShowTeaser] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(makeSessionId);
  const scrollRef = useRef(null);

  // Refs for stateful scheduling (avoid re-render loops)
  const dismissedCountRef = useRef(0);
  const lastShownAtRef = useRef(0);
  const scrollTriggeredRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());
  const hasOpenedRef = useRef(false);
  const teaserHideTimerRef = useRef(null);

  // Helper: can we show a teaser now ?
  const canShow = useCallback(() => {
    if (open || hasOpenedRef.current) return false;
    const now = Date.now();
    const gap =
      dismissedCountRef.current > 0
        ? MIN_INTERVAL_AFTER_DISMISS
        : MIN_INTERVAL_BETWEEN_TEASERS;
    return now - lastShownAtRef.current >= gap;
  }, [open]);

  const triggerTeaser = useCallback(() => {
    if (!canShow()) return;
    setShowTeaser(true);
    lastShownAtRef.current = Date.now();
    setTeaserIdx((i) => (i + 1) % TEASER_MESSAGES.length);

    clearTimeout(teaserHideTimerRef.current);
    teaserHideTimerRef.current = setTimeout(() => {
      setShowTeaser(false);
    }, TEASER_VISIBLE_DURATION);
  }, [canShow]);

  // Initial reveal (bubble visible + first teaser)
  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(true);
      // First teaser right after bubble appears
      setTimeout(triggerTeaser, 600);
    }, FIRST_APPEAR_DELAY);
    return () => clearTimeout(t);
  }, [triggerTeaser]);

  // Scroll trigger (once, at 40% scroll)
  useEffect(() => {
    const onScroll = () => {
      lastInteractionRef.current = Date.now();
      if (scrollTriggeredRef.current) return;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      if (window.scrollY / total >= SCROLL_TRIGGER_RATIO) {
        scrollTriggeredRef.current = true;
        if (visible) triggerTeaser();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [visible, triggerTeaser]);

  // Idle reminder + activity tracking
  useEffect(() => {
    const markActivity = () => {
      lastInteractionRef.current = Date.now();
    };
    window.addEventListener("mousemove", markActivity);
    window.addEventListener("keydown", markActivity);
    window.addEventListener("click", markActivity);

    const idleInterval = setInterval(() => {
      if (!visible || open || hasOpenedRef.current) return;
      const idleFor = Date.now() - lastInteractionRef.current;
      if (idleFor >= IDLE_REMINDER_MS) {
        triggerTeaser();
        lastInteractionRef.current = Date.now(); // reset to avoid chain
      }
    }, 10000);

    return () => {
      window.removeEventListener("mousemove", markActivity);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("click", markActivity);
      clearInterval(idleInterval);
    };
  }, [visible, open, triggerTeaser]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const handleBubbleClick = () => {
    setOpen((o) => !o);
    setShowTeaser(false);
    hasOpenedRef.current = true;
  };

  const dismissTeaser = (e) => {
    e.stopPropagation();
    setShowTeaser(false);
    dismissedCountRef.current += 1;
    lastShownAtRef.current = Date.now();
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.reply, lead: res.data.lead_captured },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Je rencontre un petit souci technique. Vous pouvez nous écrire à contact@emgroupdigital.com ou au +262 262 66 79 70.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            key="bubble-wrap"
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-40 flex items-end gap-3 pointer-events-none"
          >
            {/* Teaser bubble */}
            <AnimatePresence mode="wait">
              {showTeaser && !open && (
                <motion.button
                  key={`teaser-${teaserIdx}`}
                  initial={{ opacity: 0, x: 30, scale: 0.85 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 30, scale: 0.85 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  onClick={handleBubbleClick}
                  data-testid="chat-teaser"
                  className="pointer-events-auto relative mb-1 max-w-[280px] rounded-2xl rounded-br-sm bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 px-4 py-3 pr-9 text-left text-sm text-white shadow-[0_15px_40px_rgba(0,0,0,0.5)] hover:bg-[#141418]/95 transition-colors"
                >
                  <span
                    onClick={dismissTeaser}
                    className="absolute top-2 right-2 p-1 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-all"
                    aria-label="Fermer"
                  >
                    <X size={13} />
                  </span>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00FF9C] animate-pulse" />
                    Émi · EM Group
                  </div>
                  <div className="font-light leading-snug">
                    {TEASER_MESSAGES[teaserIdx]}
                  </div>
                </motion.button>
              )}
            </AnimatePresence>

            {/* Bubble button */}
            <button
              data-testid="chat-bubble"
              onClick={handleBubbleClick}
              className="pointer-events-auto relative h-14 w-14 md:h-16 md:w-16 rounded-full gradient-bg grid place-items-center shadow-[0_10px_40px_rgba(229,36,49,0.45)] hover:scale-105 transition-all duration-300 btn-shine"
              aria-label={open ? "Fermer le chat" : "Ouvrir le chat"}
            >
              {!open && showTeaser && (
                <span className="absolute inset-0 rounded-full bg-[#E52431] animate-ping opacity-30" />
              )}
              <span className="relative z-10 text-white">
                {open ? <X size={22} /> : <MessageCircle size={22} />}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-4 md:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[400px] max-w-[420px] h-[560px] max-h-[calc(100vh-8rem)] rounded-3xl overflow-hidden bg-[#0A0A0A] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] flex flex-col"
          >
            {/* Header */}
            <div className="relative p-5 bg-gradient-to-r from-[#1a0f12] to-[#0B1030] border-b border-white/10">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_0%,rgba(229,36,49,0.3),transparent_60%)]" />
              <div className="relative z-10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full gradient-bg grid place-items-center relative">
                  <Sparkles size={18} className="text-white" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#00FF9C] border-2 border-[#0A0A0A]" />
                </div>
                <div>
                  <div className="font-outfit text-white font-medium">Émi · EM Group</div>
                  <div className="text-xs text-white/55 font-light">Réponse sous 24h</div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#050507]"
            >
              {messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content} lead={m.lead} />
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white/60 text-sm">
                    <Loader2 size={14} className="animate-spin" /> Émi écrit…
                  </div>
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      data-testid={`chat-quick-${q.slice(0, 10)}`}
                      className="px-3 py-1.5 text-xs rounded-full bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 hover:border-white/25 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="p-3 border-t border-white/10 bg-[#0A0A0A] flex items-center gap-2"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écrivez votre message…"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#E52431]/60 transition-colors"
                disabled={loading}
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={loading || !input.trim()}
                className="h-11 w-11 rounded-full gradient-bg grid place-items-center text-white disabled:opacity-50 hover:scale-105 transition-all duration-200 btn-shine flex-shrink-0"
                aria-label="Envoyer"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MessageBubble({ role, content, lead }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "gradient-bg text-white rounded-br-sm"
            : "bg-white/5 border border-white/10 text-white/90 rounded-bl-sm"
        }`}
      >
        {content}
        {lead && (
          <div className="mt-2 pt-2 border-t border-white/20 text-xs text-white/70 inline-flex items-center gap-1">
            ✅ Demande enregistrée
          </div>
        )}
      </div>
    </div>
  );
}
